import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';
import DOMPurify from 'dompurify';

const sanitizeInput = (input) => DOMPurify.sanitize(input);

function CreatePrivateFirma() {
    const { user } = useUser();
    const navigate = useNavigate();
    const [form, setForm] = useState({
        firmenname: '',
        adresse: '',
        kontaktperson: '',
        email: '',
        telefon: '',
        website: '',
        status: 'offen'
    });
    const [loading, setLoading] = useState(false);
    const [setError] = useState(null);
    const [setSuccess] = useState(null);
    const [focusedField, setFocusedField] = useState(null);

    const handleFocus = (fieldName) => {
        setFocusedField(fieldName);
    };

    const handleBlur = () => {
        setFocusedField(null);
    };

    React.useEffect(() => {
        if (!user || user.rolle !== 'ADMIN') {
            navigate('/');
        }
    }, [user, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: sanitizeInput(value) }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setSuccess(null);

        try {
            const token = localStorage.getItem('token');
            const res = await fetch('https://company-application-platform-backend.onrender.com/api/private-firma', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                credentials: 'include',
                body: JSON.stringify(form)
            });
            if (res.ok) {
                navigate('/private-firmen');
            }
        } catch (err) {
            setError('Netzwerkfehler beim Erstellen der privaten Firma');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <section className="py-12">
                <div>
                    <div className="registration-container">
                        <div className="registration-card">

                            <div className="registration-header">
                                <h1 className='registration-title'>Neue Private Firma erstellen</h1>
                                <p className="registration-subtitle">
                                    Erstellen Sie einen neuen privaten Firmeneintrag für interne Verwaltungszwecke.
                                </p>
                            </div>
                            <form onSubmit={handleSubmit} className="registration-form">
                                <div className="form-grid">
                                    <div className="form-section">
                                        <div className="input-group">
                                            <label htmlFor="firmenname" className="input-label">
                                                Firmenname *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'firmenname' ? 'focused' : ''} ${form.firmenname ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                                </svg>
                                                <input
                                                    id="firmenname"
                                                    name="firmenname"
                                                    type="text"
                                                    required
                                                    value={form.firmenname}
                                                    onChange={handleChange}
                                                    className="form-input"
                                                    placeholder="Firmenname"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label htmlFor="adresse" className="input-label">
                                                Adresse *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'adresse' ? 'focused' : ''} ${form.adresse ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                </svg>
                                                <input
                                                    id="adresse"
                                                    name="adresse"
                                                    type="text"
                                                    required
                                                    value={form.adresse}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('adresse')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Musterstraße 123, 1234 Stadt"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="kontaktperson" className="input-label">
                                                Kontaktperson *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'kontaktperson' ? 'focused' : ''} ${form.kontaktperson ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                </svg>
                                                <input
                                                    id="kontaktperson"
                                                    name="kontaktperson"
                                                    type="text"
                                                    required
                                                    value={form.kontaktperson}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('kontaktperson')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="Max Mustermann"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label htmlFor="email" className="input-label">
                                                E-Mail *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'email' ? 'focused' : ''} ${form.email ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                </svg>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={form.email}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('email')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="kontakt@firma.ch"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className='form-section'>



                                        <div className="input-group">
                                            <label htmlFor="telefon" className="input-label">
                                                Telefon *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'telefon' ? 'focused' : ''} ${form.telefon ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                                </svg>
                                                <input
                                                    id="telefon"
                                                    name="telefon"
                                                    type="tel"
                                                    required
                                                    value={form.telefon}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('telefon')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="+41 79 123 45 67"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>

                                        <div className="input-group">
                                            <label htmlFor="website" className="input-label">
                                                Website *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'website' ? 'focused' : ''} ${form.website ? 'has-value' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                                <input
                                                    id="website"
                                                    name="website"
                                                    type="url"
                                                    value={form.website}
                                                    onChange={handleChange}
                                                    onFocus={() => handleFocus('website')}
                                                    onBlur={handleBlur}
                                                    className="form-input"
                                                    placeholder="https://www.firma.ch"
                                                    disabled={loading}
                                                />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label htmlFor="status" className="input-label">
                                                Status *
                                            </label>
                                            <div className={`input-wrapper ${focusedField === 'status' ? 'focused' : ''}`}>
                                                <svg className="input-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                                </svg>
                                                <select id="status" name="status" value={form.status} onChange={handleChange} onFocus={() => handleFocus('status')} onBlur={handleBlur} className="form-input status-badge" disabled={loading}>
                                                    <option value="offen">Offen</option>
                                                    <option value="in Arbeit">In Arbeit</option>
                                                    <option value="versendet">Versendet</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="form-footer">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className={`submit-button ${loading ? 'loading' : ''}`}
                                    >
                                        {loading ? (
                                            <>
                                                <svg className="loading-spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                </svg>
                                                Erstelle...
                                            </>
                                        ) : 'Private Firma erstellen'}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => navigate('/private-firmen')}
                                        disabled={loading}
                                        className={`submit-button ${loading ? 'loading' : ''}`}
                                    >
                                        Zurück zur Liste
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

export default CreatePrivateFirma;